import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { usePortalPosition } from '@/hooks/usePortalPosition';

import './SearchInput.css';

export type SearchInputProps = {
  value: string;
  onChange: (value: string) => void;
  /**
   * Called once after `debounceMs` of input idle time. Use this for
   * autocomplete/fetch triggers when callers need an event distinct from
   * every keystroke.
   */
  onDebouncedChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  /** Optional autocomplete suggestions. UI-only — fetching is the caller's responsibility. */
  suggestions?: string[];
  onSuggestionSelect?: (suggestion: string) => void;
  loading?: boolean;
  placeholder?: string;
  ariaLabel: string;
  /** Spec §M3 mandates 300 ms — only override in tests. */
  debounceMs?: number;
  disabled?: boolean;
  className?: string;
};

const DEFAULT_DEBOUNCE_MS = 300;

export function SearchInput({
  value,
  onChange,
  onDebouncedChange,
  onSubmit,
  suggestions,
  onSuggestionSelect,
  loading,
  placeholder,
  ariaLabel,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  disabled,
  className,
}: SearchInputProps) {
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const { panelStyle } = usePortalPosition({
    triggerRef: inputRef,
    open,
    matchTriggerWidth: true,
    onTriggerOutOfView: () => {
      setOpen(false);
      setActiveIdx(-1);
    },
  });

  // Debounced fan-out: callers that hand us `onDebouncedChange` get one call
  // after `debounceMs` of idle input. This is the Spec §M3 contract for
  // search/filter changes — the synchronous `onChange` still fires on every
  // keystroke for controlled-input updates.
  const lastDebouncedRef = useRef<string>(value);
  useEffect(() => {
    if (!onDebouncedChange) return;
    if (lastDebouncedRef.current === value) return;
    const id = setTimeout(() => {
      lastDebouncedRef.current = value;
      onDebouncedChange(value);
    }, debounceMs);
    return () => clearTimeout(id);
  }, [value, debounceMs, onDebouncedChange]);

  useEffect(() => {
    if (!suggestions || suggestions.length === 0) {
      setOpen(false);
      setActiveIdx(-1);
      return;
    }
    setOpen(true);
    setActiveIdx((prev) => (prev >= suggestions.length ? -1 : prev));
  }, [suggestions]);

  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      if (!rootRef.current) return;
      const target = event.target as Node;
      if (rootRef.current.contains(target)) return;
      if (listboxRef.current && listboxRef.current.contains(target)) return;
      setOpen(false);
      setActiveIdx(-1);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const clear = useCallback(() => {
    onChange('');
    inputRef.current?.focus();
  }, [onChange]);

  const commitSuggestion = (s: string) => {
    onChange(s);
    onSuggestionSelect?.(s);
    setOpen(false);
    setActiveIdx(-1);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const list = suggestions ?? [];
    if (event.key === 'Escape') {
      if (value) {
        event.preventDefault();
        clear();
      } else if (open) {
        event.preventDefault();
        setOpen(false);
      }
      return;
    }
    if (event.key === 'Enter') {
      if (open && activeIdx >= 0 && list[activeIdx]) {
        event.preventDefault();
        commitSuggestion(list[activeIdx]);
      } else {
        onSubmit?.(value);
      }
      return;
    }
    if (!open || list.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIdx((i) => (i + 1) % list.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIdx((i) => (i <= 0 ? list.length - 1 : i - 1));
    }
  };

  return (
    <div
      ref={rootRef}
      className={['search-input', disabled ? 'search-input--disabled' : null, className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="search-input__field" role="search">
        <span className="search-input__icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5.25" stroke="currentColor" strokeWidth="1.5" />
            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="search"
          value={value}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-autocomplete={suggestions ? 'list' : undefined}
          aria-controls={open ? listboxId : undefined}
          aria-expanded={suggestions ? open : undefined}
          aria-activedescendant={open && activeIdx >= 0 ? `${baseId}-opt-${activeIdx}` : undefined}
          autoComplete="off"
          disabled={disabled}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          className="search-input__input"
        />
        {loading ? (
          <span className="search-input__spinner" aria-hidden="true" />
        ) : value ? (
          <button
            type="button"
            className="search-input__clear"
            onClick={clear}
            aria-label="Clear search"
          >
            ×
          </button>
        ) : null}
      </div>
      {open && suggestions && suggestions.length > 0 && typeof document !== 'undefined'
        ? createPortal(
            <ul
              ref={listboxRef}
              id={listboxId}
              role="listbox"
              className="search-input__listbox"
              style={panelStyle}
            >
              {suggestions.map((s, i) => (
                <li
                  key={`${s}-${i}`}
                  id={`${baseId}-opt-${i}`}
                  role="option"
                  aria-selected={i === activeIdx}
                  className={`search-input__option${
                    i === activeIdx ? ' search-input__option--active' : ''
                  }`}
                  onMouseEnter={() => setActiveIdx(i)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    commitSuggestion(s);
                  }}
                >
                  {s}
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
